/**
 * Server-decided refund eligibility for the items of one order.
 *
 * ⛔ WHY A HOOK AND NOT A LOCAL RULE. Four components used to compute this themselves with a
 * hardcoded literal (`source === 'bcw' || category === 'supplies' || ...`) plus a hardcoded
 * `daysSinceDelivery <= 30`, while the server decided differently in
 * BCWRefundService.validate_refund_eligibility. Two copies of a money rule cannot be kept in
 * agreement by hoping: a customer could be shown "Returns Accepted" on an item the server then
 * refuses, or "Final Sale" on one it would have taken.
 *
 * One request per ORDER, not per item, because the account screens render a badge and a button
 * on every row.
 *
 * ⚠ UNKNOWN IS NOT ELIGIBLE. While the request is in flight, or if it fails, callers get
 * `undefined` for an item rather than a guess. A component that rendered "Returns Accepted"
 * during loading would be making exactly the claim this hook exists to stop.
 */
import { useEffect, useState } from 'react';
import { refundsAPI } from '../services/api';

export function useRefundEligibility(orderId) {
  const [byItemId, setByItemId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!orderId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    refundsAPI
      .checkOrderEligibility(orderId)
      .then((data) => {
        if (cancelled) return;
        const map = {};
        (data?.items || []).forEach((entry) => {
          map[entry.order_item_id] = entry;
        });
        setByItemId(map);
      })
      .catch((err) => {
        if (cancelled) return;
        // Recorded, not swallowed into an empty map: an empty map is indistinguishable from
        // "the server said nothing is refundable", and the caller needs to tell them apart.
        setError(err);
        setByItemId(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  return {
    loading,
    error,
    /** The server's entry for an item, or undefined while unknown. */
    forItem: (itemId) => (byItemId ? byItemId[itemId] : undefined),
  };
}

export default useRefundEligibility;
